import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from "react";

import type { CallToActionPosition } from "domains/callsToAction/helpers/calculateCallToActionPositions";
import { calculateCallToActionPositions } from "domains/callsToAction/helpers/calculateCallToActionPositions";

export type CutoutProps = { radius: number; x: number; y: number };

export type PopupProps = {
  fullscreen: boolean;
  left: number;
  position: CallToActionPosition;
  top: number;
  width: number;
};

export const useCallToAction = (container: HTMLDivElement | null, target: Element, highlightElement: boolean) => {
  const [positions, setPositions] = useState<{ cutout: CutoutProps; popup?: PopupProps }>({
    cutout: { radius: 0, x: 0, y: 0 },
  });
  const [viewport, setViewport] = useState<{ height: number; width: number }>({
    height: innerHeight,
    width: innerWidth,
  });

  useLayoutEffect(() => {
    document.body.style.overflow = "clip";

    return () => {
      document.body.style.overflow = "unset";
    };
  }, []);

  useEffect(() => {
    const listener = () => setViewport({ height: innerHeight, width: innerWidth });
    const listenerType = "resize";
    addEventListener(listenerType, listener);
    return () => removeEventListener(listenerType, listener);
  }, []);

  const updatePositions = useCallback(() => {
    if (container !== null)
      setPositions(
        calculateCallToActionPositions(
          container.getBoundingClientRect(),
          target.getBoundingClientRect(),
          viewport,
          highlightElement,
        ),
      );
  }, [container, highlightElement, target, viewport]);

  useLayoutEffect(updatePositions, [updatePositions]);

  useEffect(() => {
    const observer = new MutationObserver(updatePositions);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [updatePositions]);

  return useMemo(() => ({ ...positions, viewport }), [positions, viewport]);
};
